import {useMemo} from 'react';
import type {ComponentPropsWithRef, ComponentType} from 'react';

import * as motion from 'motion/react-m';

import {InViewPortTrigger} from 'UI/animations/shared';

import type {WithOptionalChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface BaseProps<T> {
    /**
     * The base component to be animated. This can be any valid React component or HTML element.
     * The component will be wrapped in a motion element to apply animation effects when it enters the viewport.
     */
    baseComponent: T;
    /**
     * The stagger value for the animation, determining the delay between each child element's animation.
     * A higher value creates a longer delay between each child element entering the viewport.
     *
     * @default 0.2
     */
    stagger?: number;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'ViewportTrigger'
     */
    testId?: string;
}

/**
 * Renders a motion-enabled wrapper that triggers entrance animations when the component enters the viewport.
 * This component is a thin utility around `framer-motion` that converts an arbitrary base component into an animated one via `motion.create`.
 * It applies a predefined `InViewPortTrigger` variant, starting from `outOfView` and animating to `inView` when the element enters the viewport.
 * The animation runs once (`viewport.once = true`) and supports a custom `stagger` value passed through `custom` for orchestrating child animations.
 * It also passes through any additional props to the underlying base component, and exposes a `data-cy` attribute for testing.
 *
 * @param props               The component props.
 * @param props.baseComponent The underlying element or component to be wrapped with motion behavior.
 * @param props.children      Optional children to render inside the animated base component.
 * @param props.stagger       Custom value forwarded to motion variants (via `custom`) to control staggering. Defaults to `0.2`.
 * @param props.testId        Identifier used for end-to-end testing, applied as `data-cy`. Defaults to `'ViewportTrigger'`.
 * @returns A motion-enhanced element that animates into view when it enters the viewport.
 *
 * @example
 * ```tsx
 * // Animate a <section> the first time it enters the viewport
 * <ViewportTrigger baseComponent="section" stagger={0.3}>
 *   <h2>Fade & slide in</h2>
 *   <p>This content animates when scrolled into view.</p>
 * </ViewportTrigger>
 *
 * // Animate a custom component
 * const Box = ({ className, children }: { className?: string; children?: React.ReactNode }) => (
 *   <div className={className}>{children}</div>
 * );
 *
 * <ViewportTrigger baseComponent={Box} testId="AnimatedBox">
 *   <span>Animated Box Content</span>
 * </ViewportTrigger>
 * ```
 */
const ViewportTrigger = <T,>({
    baseComponent,
    children,
    stagger = 0.2,
    testId = 'ViewportTrigger',
    ...props
// @ts-expect-error
}: WithOptionalChildren<BaseProps<T> & ComponentPropsWithRef<T>>) => {
    const Base = useMemo(() => motion.create(baseComponent as ComponentType), [baseComponent]);

    return (
        // eslint-disable-next-line react-hooks/static-components
        <Base
            custom={stagger}
            data-cy={testId}
            initial="outOfView"
            variants={InViewPortTrigger}
            viewport={{
                amount: 'some',
                margin: '0px 0px -20% 0px',
                once: true
            }}
            whileInView="inView"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
        >
            {children}
        </Base>
    );
};

ViewportTrigger.displayName = 'ViewportTrigger';

export {ViewportTrigger};